import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  InputGroup,
} from "react-bootstrap";
import {
  IndustryBenchmarks,
  AdTypeBenchmarks,
  IndustryRecommendations,
  AdTypeRecommendations,
  Metrics,
} from "./types";
// import "./MarketingRoiCalculator.css";
import { Box } from "./marketingRoiStyle";
import { isInIframe } from "../../utils/is-in-iframe";
import { ToolsHeader } from "../components/ToolsHeader";

const industryBenchmarks: IndustryBenchmarks = {
  ecommerce: { roi: 300, profitMargin: 45, efficiency: 400 },
  saas: { roi: 500, profitMargin: 70, efficiency: 600 },
  "real-estate": { roi: 250, profitMargin: 35, efficiency: 300 },
  retail: { roi: 200, profitMargin: 30, efficiency: 250 },
};

const adTypeBenchmarks: AdTypeBenchmarks = {
  "social-media": { roi: 250, profitMargin: 40, efficiency: 300 },
  sem: { roi: 300, profitMargin: 45, efficiency: 350 },
  display: { roi: 150, profitMargin: 25, efficiency: 200 },
  email: { roi: 400, profitMargin: 60, efficiency: 450 },
};

const industryRecommendations: IndustryRecommendations = {
  ecommerce: [
    "Improve product page conversion rates by adding customer reviews",
    "Run retargeting campaigns for abandoned carts",
    "Optimize product images and descriptions",
    "Leverage urgency with limited-time offers",
    "Use personalized product recommendations",
    "Optimize checkout flow to reduce drop-offs",
    "Run referral and loyalty programs",
  ],
  saas: [
    "Focus on improving trial-to-paid conversion rates",
    "Reduce churn through better onboarding",
    "Invest in content marketing",
    "Offer free trials with clear upgrade paths",
    "Leverage case studies and testimonials",
    "Use in-app messaging for upsells",
    "Run account-based marketing for enterprise clients",
  ],
  "real-estate": [
    "Run hyper-localized ad campaigns",
    "Use virtual tours in marketing",
    "Focus on local SEO",
    "Highlight neighborhood amenities in campaigns",
    "Leverage drone footage for property showcases",
    "Use lead capture forms on listings",
    "Promote open houses through targeted ads",
  ],
  retail: [
    "Implement loyalty programs",
    "Use seasonal promotions",
    "Optimize in-store conversion tracking",
    "Promote best-selling products",
    "Leverage geotargeted mobile ads",
    "Bundle products for higher cart value",
    "Offer click-and-collect options",
  ],
};

const adTypeRecommendations: AdTypeRecommendations = {
  "social-media": [
    "Optimize for mobile-first content",
    "Use carousel ads for multiple products",
    "A/B test ad creatives",
    "Leverage influencer collaborations",
    "Use video ads for storytelling",
    "Test interactive polls and quizzes",
    "Retarget engaged followers with offers",
  ],
  sem: [
    "Focus on long-tail keywords",
    "Optimize landing pages",
    "Monitor quality score",
    "Use ad extensions for more visibility",
    "Leverage dynamic search ads",
    "Test bidding strategies (manual vs. automated)",
    "Regularly update negative keyword lists",
  ],
  display: [
    "Use engaging visuals",
    "Implement frequency capping",
    "Target specific placements",
    "Leverage animated or rich media ads",
    "Use contextual targeting",
    "Run remarketing display campaigns",
    "Test different banner sizes",
  ],
  email: [
    "Segment your email list",
    "Personalize subject lines",
    "Test different send times",
    "Optimize for mobile viewing",
    "Use automation for drip campaigns",
    "A/B test CTAs and layouts",
    "Clean inactive subscribers regularly",
  ],
};

const MarketingRoiCalculator: React.FC = () => {
  const [revenue, setRevenue] = useState<number | "">("");
  const [marketingCost, setMarketingCost] = useState<number | "">("");
  const [industry, setIndustry] =
    useState<keyof IndustryBenchmarks>("ecommerce");
  const [adType, setAdType] = useState<keyof AdTypeBenchmarks>("social-media");
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [industryRecommendation, setIndustryRecommendation] = useState<
    string[]
  >([]);
  const [adTypeRecommendation, setAdTypeRecommendation] = useState<string[]>(
    []
  );

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [showResults, metrics]);
  const calculateROI = () => {
    if (
      revenue === "" ||
      marketingCost === "" ||
      revenue <= 0 ||
      marketingCost <= 0
    ) {
      alert("Please enter valid revenue and marketing cost values.");
      return;
    }

    const calculatedMetrics: Metrics = {
      roi: ((revenue - marketingCost) / marketingCost) * 100,
      profitMargin: ((revenue - marketingCost) / revenue) * 100,
      breakEven: marketingCost,
      efficiency: (revenue / marketingCost) * 100,
    };

    setMetrics(calculatedMetrics);
    setShowResults(true);
    setIndustryRecommendation(
      getRandomRecommendations(industryRecommendations[industry])
    );
    setAdTypeRecommendation(
      getRandomRecommendations(adTypeRecommendations[adType])
    );
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  };
  const getRandomRecommendations = (arr: string[], count = 3) => {
    const shuffled = [...arr].sort(() => 0.3 - Math.random());
    return shuffled.slice(0, count);
  };
  const resetForm = () => {
    setRevenue("");
    setMarketingCost("");
    setIndustry("ecommerce");
    setAdType("social-media");
    setMetrics(null);
    setShowResults(false);
  };

  const getMetricTitle = (metric: keyof Omit<Metrics, "breakEven">) => {
    switch (metric) {
      case "roi":
        return "ROI Benchmark";
      case "profitMargin":
        return "Profit Margin Benchmark";
      case "efficiency":
        return "Efficiency Benchmark";
      default:
        return "";
    }
  };

  const renderBenchmarkCard = (
    metric: keyof Omit<Metrics, "breakEven">,
    actual: number,
    benchmark: number
  ) => {
    const difference = ((actual - benchmark) / benchmark) * 100;
    const arrow = difference > 0 ? "↑" : difference < 0 ? "↓" : "→";
    const comparisonClass =
      difference > 0 ? "success" : difference < 0 ? "error" : "warning";
    const comparisonText = `${Math.abs(difference).toFixed(1)}% ${
      difference > 0 ? "above" : difference < 0 ? "below" : "at"
    } average`;

    return (
      <Card className="benchmark-card">
        <Card.Body>
          <div className="benchmark-title">{getMetricTitle(metric)}</div>
          <div className="benchmark-value">{benchmark.toFixed(1)}%</div>
          <div className="benchmark-comparison">
            <span className={`benchmark-arrow ${comparisonClass}`}>
              {arrow}
            </span>
            <span className={comparisonClass}>{comparisonText}</span>
          </div>
        </Card.Body>
      </Card>
    );
  };

  return (
    <Box>
      <Container
        className={`marketing-roi-container p-0 ${
          !isInIframe ? "mt-5 defaultText" : "mt-0 interDisplay"
        }`}
      >
        {!isInIframe && (
          <ToolsHeader
            title="Marketing ROI Calculator"
            description="Calculate and analyze your marketing campaign performance to
                improve results and ROI."
          />
        )}
        {/* <div className="header text-center mb-4">
          <h1>Marketing ROI Calculator</h1>
          <p>Calculate and analyze your marketing campaign performance</p>
        </div> */}

        <Card className="card">
          <Card.Body className="cardFormRoi">
            <Row>
              <Col md={6}>
                <Form.Group className="form-group" controlId="revenue">
                  <Form.Label>Revenue</Form.Label>
                  <InputGroup className="custom-input-group">
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      placeholder="Enter total revenue"
                      value={revenue}
                      onChange={(e) =>
                        setRevenue(
                          e.target.value === ""
                            ? ""
                            : parseFloat(e.target.value)
                        )
                      }
                    />
                  </InputGroup>
                </Form.Group>
                <Form.Group className="form-group" controlId="marketingCost">
                  <Form.Label>Marketing Cost</Form.Label>
                  <InputGroup className="custom-input-group">
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      placeholder="Enter marketing cost"
                      value={marketingCost}
                      onChange={(e) =>
                        setMarketingCost(
                          e.target.value === ""
                            ? ""
                            : parseFloat(e.target.value)
                        )
                      }
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="form-group" controlId="industry">
                  <Form.Label>Industry</Form.Label>
                  <Form.Select
                    value={industry}
                    onChange={(e) =>
                      setIndustry(e.target.value as keyof IndustryBenchmarks)
                    }
                  >
                    <option value="ecommerce">E-commerce</option>
                    <option value="saas">SaaS</option>
                    <option value="real-estate">Real Estate</option>
                    <option value="retail">Retail</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="form-group" controlId="adType">
                  <Form.Label>Ad Type</Form.Label>
                  <Form.Select
                    value={adType}
                    onChange={(e) =>
                      setAdType(e.target.value as keyof AdTypeBenchmarks)
                    }
                  >
                    <option value="social-media">Social Media Ads</option>
                    <option value="sem">Search Engine Marketing</option>
                    <option value="display">Display Ads</option>
                    <option value="email">Email Marketing</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <div className="button-group d-flex justify-content-end gap-3">
              <Button
                variant="outline-secondary"
                className="button-secondary"
                onClick={resetForm}
              >
                Reset
              </Button>
              <Button
                variant="primary"
                className="button-primary"
                onClick={calculateROI}
              >
                Calculate
              </Button>
            </div>
          </Card.Body>
        </Card>

        {showResults && metrics && (
          <Card className="card">
            <Card.Body className="cardFormRoi">
              <div className="section-title d-flex align-items-center gap-3 mb-4">
                <div className="section-icon">📊</div>
                <h2>Key Metrics</h2>
              </div>
              <Row className="metrics-grid">
                <Col xs={12}>
                  <Card className="metric-card blue">
                    <Card.Body>
                      <div className="metric-icon">📈</div>
                      <div className="metric-title">ROI</div>
                      <div className="metric-value">
                        {metrics.roi.toFixed(2)}%
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12}>
                  <Card className="metric-card green">
                    <Card.Body>
                      <div className="metric-icon">🎯</div>
                      <div className="metric-title">Profit Margin</div>
                      <div className="metric-value">
                        {metrics.profitMargin.toFixed(2)}%
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12}>
                  <Card className="metric-card purple">
                    <Card.Body>
                      <div className="metric-icon">💰</div>
                      <div className="metric-title">Break-Even</div>
                      <div className="metric-value">
                        ${metrics.breakEven?.toFixed(2) ?? "0"}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12}>
                  <Card className="metric-card orange">
                    <Card.Body>
                      <div className="metric-icon">📊</div>
                      <div className="metric-title">Efficiency</div>
                      <div className="metric-value">
                        {metrics.efficiency.toFixed(2)}%
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <div className="section-title d-flex align-items-center gap-3 mb-4">
                <div className="section-icon">📈</div>
                <h2>Industry Benchmarks</h2>
              </div>
              <Row className="benchmarks-grid">
                {(["roi", "profitMargin", "efficiency"] as const).map(
                  (metric) => (
                    <Col key={metric}>
                      {renderBenchmarkCard(
                        metric,
                        metrics[metric]!,
                        (industryBenchmarks[industry][metric] +
                          adTypeBenchmarks[adType][metric]) /
                          2
                      )}
                    </Col>
                  )
                )}
              </Row>

              <div className="section-title d-flex align-items-center gap-3 mb-4">
                <div className="section-icon">💡</div>
                <h2>Strategic Recommendations</h2>
              </div>
              <Row className="recommendations-grid">
                <Col xs={12}>
                  <div className="recommendation-group">
                    <h3>Industry-Specific</h3>
                    {industryRecommendation.map((rec, index) => (
                      <Card key={index} className="recommendation-card">
                        <Card.Body className="cardFormRoi">{rec}</Card.Body>
                      </Card>
                    ))}
                  </div>
                </Col>
                <Col xs={12}>
                  <div className="recommendation-group">
                    <h3>Ad Type Optimization</h3>
                    {adTypeRecommendation.map((rec, index) => (
                      <Card key={index} className="recommendation-card alt">
                        <Card.Body className="cardFormRoi">{rec}</Card.Body>
                      </Card>
                    ))}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default MarketingRoiCalculator;
